import { Events, HttpStatusCode } from "../../../thor.enums";
import { MediaType } from "../../../typings/thor";

type RankedFile = File & { rank: number };

export class ImportMediaService {
    public static $inject: string[] = [
        "$log",
        "$q",
        "$rootScope",
        "$translate",
        "itemService",
        "imageService",
        "uploadService"
    ];

    private static CHUNK_SIZE = 6;

    protected $t;
    protected sale;
    protected summary;

    private globalDeferred;

    constructor(
        protected $log: ng.ILogService,
        protected $q: ng.IQService,
        protected $rootScope: ng.IRootScopeService,
        $translate: ng.translate.ITranslateService,
        protected itemService,
        private imageService,
        private uploadService
    ) {
        this.$t = $translate.instant;
    }

    public engine(sale, files: RankedFile[]) {
        this.sale = sale;
        this.globalDeferred = this.$q.defer();

        this.summary = {
            items: {
                created: 0
            },
            errors: [],
            warnings: []
        };

        this.$q
            .seriesChunks(this.groupFilesByItems(files), ImportMediaService.CHUNK_SIZE, this.engineFiles.bind(this))
            .then(() => {
                this.sendSummary();
                this.$rootScope.$broadcast(Events.PostMessageToV2);
                this.globalDeferred.resolve();
            })
            .catch(error => {
                this.sendSummary();
                this.globalDeferred.reject(error);
            });

        return this.globalDeferred.promise;
    }

    protected createItemFromMedias(medias) {
        const filteredMedias = medias.filter(v => v);
        if (!filteredMedias.length) {
            return this.$q.reject();
        }
        return this.createItem()
            .then(item => {
                return this.$q.all(filteredMedias.map(media => this.createItemsMedias(item, media)))
                    .then(() => this.summary.items.created++);
            });
    }

    protected createItem() {
        const body = {
            category_id: this.sale.resource.category_id
        };

        return this.sale.createItem(body)
            .then(item => {
                return this.$q.resolve(item);
            })
            .catch(error => {
                this.$log.error("Error while creating item", error);
                return this.$q.reject(error);
            });
    }

    protected createItemsMedias (item, media) {
        const body = {
            media_id: media.id,
            type: MediaType.ItemPicture,
            rank: media.rank || 0
        };

        return this.itemService
            .createMedia(item.id, body)
            .then(() => {
                return this.$q.resolve();
            })
            .catch(error => {
                this.$log.error("Error while created item media", error);
                return this.$q.reject(error);
            });
    }

    protected notify(action, data) {
        this.globalDeferred.notify({action: action, data: data});
    }

    protected handleErrors(file, error) {
        const errorMessage =
            error.status === HttpStatusCode.PayloadTooLarge
                ? this.$t("fichier_n_a_pas_pu_etre_importe_la_taille_du_fichier_ne_doit_pas_exceder_n_mo", {
                      FILE: file.name,
                      N: "10"
                  })
                : this.$t("erreur_lors_de_la_creation_du_lot", { REFERENCE: file.name });

        this.notify("incrementGauge", { gauge: "medias", counter: "error" });
        this.summary.errors.push({
            message: errorMessage
        });
        // only a warning, keep process
        return this.$q.resolve();
    }

    private sendSummary() {
        this.notify("setSummary", this.summary);
    }

    private engineFiles(files: RankedFile[]) {
        return this.uploadImages(files)
            .then(medias => this.createItemFromMedias(medias));
    }

    private uploadImages(files: RankedFile[]) {
        return this.$q.all(
            files.map(
                file => this.imageService.resizeImage(file)
                    .then(
                        (blob: Blob) => this.uploadImage(file.name, blob)
                            .then(media => {
                                this.notify("incrementGauge", { gauge: "medias", counter: "valid" });
                                media.rank = file.rank;
                                return media;
                            })
                    )
                    .catch(error => this.handleErrors(file, error))
            )
        );
    }

    private uploadImage(filename, blob) {
        const content = {
            type: MediaType.ItemPicture,
            meta: {
                name: filename
            }
        };

        return this.uploadService.uploadFile(blob, content)
            .then(media =>  this.$q.resolve(media))
            .catch(error => {
                this.$log.error("Error while upload images", error);
                return this.$q.reject(error);
            });
    }

    private groupFilesByItems(files: RankedFile[]): RankedFile[][] {
        return this.reRankFiles(Object.values(files.reduce(
            (acc, file) => {
                const filename = file.name.substr(0, file.name.lastIndexOf("."));
                const {
                    // @ts-ignore
                    groups: { primary, secondary, rank }
                } = filename.match(/^(?<primary>\d+)(?:[\s.\-](?<secondary>[a-zA-Z0-9]+))?(?:_(?<rank>\d+))?$/)
                    || { groups: {} };
                const name = primary ? `${primary}${secondary ? `-${secondary}` : ""}` : filename;

                file.rank = rank ? Number(rank) : 0;
                if (acc[name]) {
                    acc[name].push(file);
                } else {
                    acc[name] = [file];
                }
                return acc;
            },
            {} as Record<string, RankedFile[]>))
        );
    }

    /**
     * Rank must start by 0 and can't have gaps
     * @param filesByItems
     * @private
     */
    private reRankFiles(filesByItems: RankedFile[][]): RankedFile[][] {
       filesByItems.forEach(files => {
           files
               .sort((a, b) => a.rank - b.rank)
               .forEach((file, idx) => {
                   file.rank = idx;
               });
       });
       return filesByItems;
    }
}

